import { isTicDisabledForEnvironment } from '@timelyapp/integration-cloud-sdk/api/api.environment.utils';
import type { DropdownMenuItem, DropdownMenuItemOnAction } from '@timelyapp/react';
import { Button, ButtonGroup, DropdownMenu } from '@timelyapp/react';
import classNames from 'classnames';
import { BorderlessStar, Cog, Plug } from 'components/Icons';
import Input from 'components/Input';
import InputDecorator from 'components/InputDecorator';
import Surface from 'components/Surface';
import ContextualTooltip from 'components/tooltips/contextual';
import { HAS_IGNORED_MEMORY } from 'constants/hints/onboarding';
import { HAS_ENABLED_TIC, HIDE_SHORT_DURATION_MEMORY_TITLES } from 'constants/user';
import type { FactoryApp } from 'factories/apps';
import type { MemoryEmptyStates } from 'factories/timeline';
import React, { useCallback, useMemo } from 'react';
import type { toggleAppsListModal } from 'redux/modules/apps.actions';
import type { setFilter, toggleDisplayRuleForMode } from 'redux/modules/timeline.actions';
import type { Filter, setTimelineStyle } from 'redux/modules/timeline.types';
import { TimelineSortTypes, TimelineStyleTypes } from 'redux/modules/timeline.types';
import type { saveUserProperty, setTicMemoryViewmode } from 'redux/modules/user.actions';
import type { MappedAction } from 'types/redux';
import type { DisplayRules } from 'types/timeline';
import debug from 'utils/debug';
import { STOP_PROPAGATION } from 'utils/react';
import { pluralize } from 'utils/text-helper';
import { notNull } from 'utils/typesafe';
import IgnoredMemoryHint from './IgnoredMemoryHint';
import styles from './MemorySidebarHeading.module.scss';
import useTimelineSnappingSettings from './tic/useTimelineSnappingSettings';

/** Maximum number of integrations shown in the integration button tooltip. */
const MAX_INTEGRATIONS = 12;

interface MemorySidebarHeadingProps {
  apps: Array<FactoryApp>;
  displayRules: DisplayRules;
  emptyStateType?: MemoryEmptyStates;
  filter: Filter;
  hasEnabledTic: boolean;
  hasTicWorkspaceSupport: boolean;
  hideShortDurationMemoryTitles: boolean;
  isAppInitialized?: boolean;
  isExperimentalUser: boolean;
  saveUserProperty: MappedAction<typeof saveUserProperty>;
  setFilter: MappedAction<typeof setFilter>;
  setTimelineStyle: MappedAction<typeof setTimelineStyle>;
  setTicMemoryViewmode: MappedAction<typeof setTicMemoryViewmode>;
  showEmptyState?: boolean;
  showIgnoredMemoryHint?: boolean;
  title: string;
  toggleAppsListModal: MappedAction<typeof toggleAppsListModal>;
  toggleDisplayRuleForMode: MappedAction<typeof toggleDisplayRuleForMode>;
}

type IntegrationsButtonProps = Pick<MemorySidebarHeadingProps, 'apps'> & {
  toggleAppsListModal: MappedAction<typeof toggleAppsListModal> | (() => void);
  maxIntegrations?: number;
  size?: 'xsmall' | 'small' | 'medium' | 'large';
  showText?: boolean;
  priority?: 'low' | 'medium' | 'high';
  hideTooltip?: boolean;
  icon?: React.ReactNode;
};

export const IntegrationsButton = React.memo<IntegrationsButtonProps>(
  ({
    apps,
    maxIntegrations = MAX_INTEGRATIONS,
    size,
    showText,
    priority = 'low',
    toggleAppsListModal,
    hideTooltip = false,
    icon,
  }) => {
    const tooltipContent =
      !hideTooltip && apps.length ? (
        <ul className={styles.integrationsTooltip}>
          {apps.slice(0, maxIntegrations).map(app => {
            return (
              <li key={app.appId}>
                <img src={app.logoUrl} width={20} height={20} role="presentation" />
                <span className={styles.name}>{app.displayName}</span>
                <span
                  className={classNames(styles.dot, {
                    [styles.connected]: app.integrated,
                    [styles.disconnected]: !app.integrated || app.ui.isDisconnected,
                  })}
                />
              </li>
            );
          })}
          {apps.length > maxIntegrations && (
            <li key={`${apps.length - maxIntegrations}-more`} className={styles.more}>
              <span className={styles.name}>+ {apps.length - maxIntegrations} more</span>
            </li>
          )}
        </ul>
      ) : undefined;

    return (
      <Button
        className={styles.button}
        priority={priority}
        size={size}
        onClick={toggleAppsListModal}
        data-testid="timeline-integrations-button"
        tooltip={tooltipContent}
      >
        {icon ?? <Plug />}
        {apps.length > 0 && apps.length}
        {showText &&
          (apps.length > 0
            ? ` ${pluralize(apps.length, {
                singular: 'integration',
                plural: 'integrations',
                omitAmount: true,
              })}`
            : ' Add integration')}
      </Button>
    );
  }
);

IntegrationsButton.displayName = 'IntegrationsButton';

const MemorySidebarHeading: React.FC<MemorySidebarHeadingProps> = ({
  apps,
  displayRules,
  emptyStateType,
  filter,
  hasTicWorkspaceSupport,
  hideShortDurationMemoryTitles,
  isAppInitialized,
  isExperimentalUser,
  setFilter,
  saveUserProperty,
  setTimelineStyle,
  setTicMemoryViewmode,
  showEmptyState,
  showIgnoredMemoryHint,
  title = 'Memory',
  toggleAppsListModal,
  toggleDisplayRuleForMode,
}) => {
  const handleEnableTic = useCallback(
    (mode: 'timeline' | 'group') => {
      setTicMemoryViewmode(mode === 'timeline' ? 'timeline' : 'list');
      saveUserProperty(HAS_ENABLED_TIC, true);
    },
    [setTicMemoryViewmode, saveUserProperty]
  );

  const modeOptions = useMemo(
    () => [
      {
        key: 'mode' as keyof Filter,
        value: 'timeline',
        label: 'Timeline',
        'data-testid': 'timeline-mode-button',
      },
      {
        key: 'mode' as keyof Filter,
        value: 'group',
        label: 'List',
        'data-testid': 'list-mode-button',
      },
    ],
    []
  );

  const hasNoMemoryApps = showEmptyState && emptyStateType === 'no-memory-apps';

  const handleSearchQueryChange = useCallback(
    (value: string) => setFilter({ key: 'searchQuery', value }),
    [setFilter]
  );
  const searchInputContent = useMemo(() => {
    return (
      <InputDecorator
        size="medium"
        faIcon="search"
        onClear={() => handleSearchQueryChange('')}
        isClearable
        hideClearButton={!filter.searchQuery}
      >
        <Input
          onKeyDown={STOP_PROPAGATION} // Menu steals focus on keydown
          value={filter.searchQuery}
          onChange={event => handleSearchQueryChange(event.currentTarget.value)}
          style={{ fontWeight: 'normal' }}
        />
      </InputDecorator>
    );
  }, [filter.searchQuery, handleSearchQueryChange]);

  const snappingSettings = useTimelineSnappingSettings();

  const dropdownOptions = useMemo(() => {
    const options: Array<DropdownMenuItem | null> = [
      snappingSettings.dropdownItem,
      { type: 'separator' },
      { type: 'label', label: 'Show' },
      {
        action: 'other',
        label: 'Unlogged',
        checked: displayRules.showUnlogged,
        type: 'checkboxItem',
      },
      filter.mode === 'timeline'
        ? {
            action: 'logged',
            label: 'Logged',
            checked: displayRules.showLogged,
            type: 'checkboxItem',
          }
        : null,
      {
        action: 'spam',
        label: 'Ignored',
        checked: displayRules.showSpam,
        type: 'checkboxItem',
      },
      filter.mode === 'timeline'
        ? {
            action: 'hideShortDurationMemoryTitles',
            label: 'Titles of short memories',
            checked: !hideShortDurationMemoryTitles,
            type: 'checkboxItem',
          }
        : null,
    ];

    if (isExperimentalUser) {
      options.push(
        { type: 'separator' },
        {
          type: 'radioGroup',
          label: 'Timeline Sort',
          icon: <BorderlessStar />,
          currentValue: filter.timelineSort,
          onValueChange: value => {
            setFilter({ key: 'timelineSort', value: value as TimelineSortTypes });
          },
          subOptions: [
            {
              type: 'radioItem',
              value: TimelineSortTypes.sortByGroupDuration,
              label: 'Group Duration (default)',
            },
            {
              type: 'radioItem',
              value: TimelineSortTypes.sortByTime,
              label: 'Time',
            },
            {
              type: 'radioItem',
              value: TimelineSortTypes.sortByAiImportance,
              label: 'AI Importance',
            },
          ],
        }
      );

      options.push(
        { type: 'separator' },

        {
          type: 'radioGroup',
          label: 'Timeline Style',
          icon: <BorderlessStar />,
          currentValue: displayRules.timelineStyle,
          onValueChange: value => {
            setTimelineStyle(value as TimelineStyleTypes);
          },
          subOptions: [
            {
              type: 'radioItem',
              value: TimelineStyleTypes.timespansOnTimeline,
              label: 'Timespans on Timeline (default)',
            },
            {
              type: 'radioItem',
              value: TimelineStyleTypes.timeEntriesOnTimeline,
              label: 'Time entries on Timeline',
            },
          ],
        }
      );
    }

    options.push(
      { type: 'separator' },
      { type: 'label', label: 'Filter' },
      { type: 'custom', content: searchInputContent }
    );

    return options.filter<DropdownMenuItem>(notNull);
  }, [
    displayRules.showLogged,
    displayRules.showSpam,
    displayRules.showUnlogged,
    displayRules.timelineStyle,
    filter.mode,
    filter.timelineSort,
    hideShortDurationMemoryTitles,
    isExperimentalUser,
    searchInputContent,
    setFilter,
    setTimelineStyle,
    snappingSettings.dropdownItem,
  ]);

  const handleOptionClick = useCallback<DropdownMenuItemOnAction>(
    params => {
      if ('action' in params.item) {
        const action = params.item.action;
        switch (action) {
          case 'other':
          case 'logged':
          case 'spam':
            toggleDisplayRuleForMode(action);
            return;

          case 'hideShortDurationMemoryTitles':
            saveUserProperty(HIDE_SHORT_DURATION_MEMORY_TITLES, !hideShortDurationMemoryTitles);
            return;

          default:
            debug.error('Unknown option action:', action);
            return;
        }
      }
    },
    [toggleDisplayRuleForMode, saveUserProperty, hideShortDurationMemoryTitles]
  );

  const handleOptionsDropdownOpen = useCallback(() => {
    if (showIgnoredMemoryHint) {
      saveUserProperty(HAS_IGNORED_MEMORY, 2);
    }
  }, [saveUserProperty, showIgnoredMemoryHint]);

  const disableTic = isTicDisabledForEnvironment();

  return (
    <Surface className={styles.container}>
      <h2 className={styles.brand}>
        <span>{hasNoMemoryApps || !isAppInitialized ? 'Automatic time tracking' : title}</span>
        <span style={{ display: 'inline-block', marginLeft: 10 }}>
          <ContextualTooltip
            title="Automatic time tracking"
            description="Your recorded work activity is shown here and can be used to log time entries."
            learnMoreUrl="https://support.memory.ai/en/articles/4351294-memory-101"
            contextIsPrivate
            testId={`contextual-tooltip-${filter.mode === 'timeline' ? 'timeline' : 'list'}`}
          >
            {filter.mode === 'timeline' ? (
              <ContextualTooltip.Timeline timeFormat={filter.timeFormat} />
            ) : (
              <ContextualTooltip.List />
            )}
          </ContextualTooltip>
        </span>
      </h2>
      <ButtonGroup
        options={modeOptions}
        activeValue={filter.mode}
        onChange={mode => setFilter({ key: 'mode', value: mode })}
      />
      <div className={styles.extraActions}>
        {hasTicWorkspaceSupport && !disableTic && (
          <Button
            size="small"
            selected
            onClick={() => handleEnableTic(filter.mode)}
            data-testid="tic-opt-in"
          >
            Try the new {filter.mode === 'timeline' ? 'Timeline' : 'List view'}!
          </Button>
        )}
        {!hasNoMemoryApps && (
          <>
            <IntegrationsButton apps={apps} toggleAppsListModal={toggleAppsListModal} />
            <DropdownMenu
              closeOnSelect={false}
              onAction={handleOptionClick}
              onOpenChange={handleOptionsDropdownOpen}
              items={dropdownOptions}
              trigger={
                <Button
                  className={styles.button}
                  priority="low"
                  data-testid="timeline-options-button"
                  tooltip={<span style={{ whiteSpace: 'nowrap' }}>Timeline options</span>}
                >
                  <IgnoredMemoryHint show={showIgnoredMemoryHint}>
                    <Cog />
                  </IgnoredMemoryHint>
                </Button>
              }
            />
          </>
        )}
      </div>
    </Surface>
  );
};

export default MemorySidebarHeading;
